import { View, Center, useTheme } from 'native-base'
import React, { useEffect } from 'react'
import { StyleSheet } from 'react-native'
import Animated, {
  Easing,
  Extrapolate,
  interpolate,
  SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated'

import { ColorNames } from '~types/native-base'
import { getColorValue } from '~utils'
type HalfCircleProps = {
  progress: SharedValue<number>
  isFlipped: boolean
  color: string
  size: number
  thickness: number
}

const HalfCircle = ({ thickness, size, progress, isFlipped = false, color }: HalfCircleProps) => {
  const fullCircleStyle = {
    width: size,
    height: size,
    borderRadius: size / 2,
  } as const

  const halfCircleContainerStyle = {
    width: size / 2,
    height: size,
    overflow: 'hidden',
  } as const

  const animatedStyle = useAnimatedStyle(() => {
    if (progress.value > 1) {
      const rotateValue = interpolate(
        progress.value,
        isFlipped ? [1, 1.5] : [1.5, 2],
        isFlipped ? [0, -180] : [0, 180],
        Extrapolate.CLAMP
      )

      return {
        transform: [
          {
            rotate: `${rotateValue}deg`,
          },
        ],
      }
    }
    const rotateValue = interpolate(
      progress.value,
      isFlipped ? [0, 0.5] : [0.5, 1],
      isFlipped ? [180, 0] : [-180, 0],
      Extrapolate.CLAMP
    )

    return {
      transform: [
        {
          rotate: `${rotateValue}deg`,
        },
      ],
    }
  })

  return (
    <View
      pointerEvents="none"
      style={{
        ...halfCircleContainerStyle,
        transform: [{ scaleX: isFlipped ? -1 : 1 }],
      }}
    >
      <Animated.View
        style={[
          {
            width: size,
            height: size,
          },
          animatedStyle,
        ]}
      >
        <View style={halfCircleContainerStyle}>
          <View
            style={{
              ...fullCircleStyle,
              borderWidth: thickness,
              borderColor: color,
            }}
          />
        </View>
      </Animated.View>
    </View>
  )
}

type LoaderProps = {
  size?: number
  color?: ColorNames
  thickness?: number
}
export const Loader = ({ size = 32, thickness = 2, color = 'actionBlue' }: LoaderProps) => {
  const { colors } = useTheme()

  const loaderColor = getColorValue({ color, colors }) || color

  const progress = useSharedValue(0)

  const easing = Easing.bezier(0.33, 0, 0.68, 1)

  useEffect(() => {
    progress.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 1000, easing }),
        withTiming(2, { duration: 1000, easing })
      ),
      -1
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fullCircleStyle = {
    width: size,
    height: size,
    borderRadius: size / 2,
  } as const

  const animatedStyle = useAnimatedStyle(() => {
    const rotateValue = interpolate(progress.value, [0, 2], [0, 720], Extrapolate.CLAMP)

    return {
      transform: [
        {
          rotate: `${rotateValue}deg`,
        },
      ],
    }
  }, [])

  const circleStyleProps = {
    color: loaderColor,
    size,
    thickness,
  }

  return (
    <Animated.View style={[animatedStyle, fullCircleStyle, styles.animatedContainer]}>
      <HalfCircle progress={progress} isFlipped={false} {...circleStyleProps} />
      <HalfCircle progress={progress} isFlipped={true} {...circleStyleProps} />
    </Animated.View>
  )
}

export const LoadingState = (props: LoaderProps) => {
  return (
    <Center flex={1}>
      <Loader {...props} />
    </Center>
  )
}

const styles = StyleSheet.create({
  animatedContainer: {
    flexDirection: 'row',
  },
})
